/**
 * The drag strip on the right edge of a resizable <th>. Its parent must be `relative`.
 *
 * `stopPropagation` on both mousedown and click is load-bearing: the header cell itself is
 * usually a sort toggle, and without it grabbing the handle would also re-sort the table.
 * (The click that ENDS a drag is swallowed separately — see `shouldSuppressClick` in
 * useResizableColumns.)
 *
 * Keyboard (design audit 2026-10-07, S3-03): the handle was drag-only, so a column could be
 * widened or reset only with a mouse. Arrow keys resize it in steps once the separator has
 * focus; Enter/Space resets it, the keyboard equivalent of the double-click. The arrow-key step
 * replays through `onStart` — the same callback the drag already uses — as a synthetic
 * mousemove/mouseup pair, rather than adding a second width-setting path to every one of this
 * component's callers: `useResizableColumns`'s drag listener computes the next width from
 * `ev.clientX - startX`, so starting at 0 and "moving" to ±step is a plain, already-correct
 * resize of exactly that size.
 *
 * The dispatch is deferred a tick: `onStart` only sets React state, and `useResizableColumns`
 * attaches its `window` mousemove/mouseup listeners from an effect, which does not run until
 * after this handler returns and React commits. Dispatching synchronously fired the synthetic
 * events into a page with no listener yet attached — the width never changed, and resizing was
 * left "on" (cursor forced to col-resize) until an unrelated real mouseup happened to clear it.
 */
interface Props {
    label: string;
    active: boolean;
    onStart: (clientX: number) => void;
    onReset: () => void;
}

const KEY_STEP = 10;
const KEY_STEP_LARGE = 50;

export function ColumnResizeHandle({ label, active, onStart, onReset }: Props) {
    const onKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const step = e.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
            const delta = e.key === 'ArrowRight' ? step : -step;
            onStart(0);
            setTimeout(() => {
                window.dispatchEvent(new MouseEvent('mousemove', { clientX: delta }));
                window.dispatchEvent(new MouseEvent('mouseup'));
            }, 0);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onReset();
        }
    };

    return (
        <span
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${label}`}
            tabIndex={0}
            onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                onStart(e.clientX);
            }}
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => { e.stopPropagation(); onReset(); }}
            onKeyDown={onKeyDown}
            title="Drag to resize · double-click or Enter to reset · arrow keys when focused"
            className={`absolute right-0 top-0 z-10 h-full w-2 cursor-col-resize border-r-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                active ? 'border-blue-500' : 'border-transparent hover:border-blue-300'
            }`}
        />
    );
}
